import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type KeyboardCoordinateGetter,
  type KeyboardSensorOptions,
  type PointerActivationConstraint,
  type PointerSensorOptions,
  type PointerSensorProps,
} from "@dnd-kit/core";

// The donor's drag gesture, as `@dnd-kit` pieces — shared by the two
// `react-beautiful-dnd` surfaces this app ports: the image strip
// (`image-upload-field.tsx`, #378) and the sort popover's reorderable rows
// (`sortable-rows.tsx`, #690). rbd is unmaintained and React-19-hostile, so
// the *gesture* ports and the library does not (#679 Q8); everything here was
// read off rbd 13.1.1 and, for the strip, driven on the running donor.
//
// Hoisted out of the strip rather than copied, so the two gestures cannot
// drift: the pointer sensor with rbd's mouse and touch rules, the keyboard's
// key set, the keys a drag swallows and the gestures that call a keyboard drag
// off, the screen-reader instructions, and rbd's two motion curves with its
// distance-scaled drop. What stays with each surface is its own geometry — the
// strip's wrapping rows and collision rule, the list's vertical walk.

/**
 * Tab and Enter, swallowed for the length of **either** kind of drag.
 *
 * `prevent-standard-key-events.js:6-11` is reached from the tail of both of
 * rbd's sensors — the keyboard one's `keydown` binding (`use-keyboard-sensor
 * .js:93-96`) and the mouse one's (`use-mouse-sensor.js:158`) — so a mouse
 * drag swallows them too. That half is the one that bites: the image strip sits
 * in an edit form and inside the Request Item dialog, and the sort rows inside
 * a popover over a form, so an Enter that reaches the page while an item is in
 * the air submits the form out from under the gesture.
 */
const KEYS_ANY_DRAG_SWALLOWS = new Set(["Tab", "Enter"]);

/**
 * The keys only a *keyboard* drag swallows, and `@dnd-kit` lets through.
 *
 * rbd binds `keydown` on the window with `capture: true` and `preventDefault`s
 * all four arrows unconditionally (`use-keyboard-sensor.js:66-89`) plus
 * PageUp/PageDown, Home and End as `scrollJumpKeys` (`:24-30,91-95`).
 * `@dnd-kit` prevents only the keys it *acts on*, and the vertical arrows and
 * the scroll-jump keys it never acts on at all — so without this, on a
 * scrolled item-edit form, the page walks out from under the lifted tile.
 *
 * ←/→ are **not** here, and not by oversight: `sortableKeyboardCoordinates`
 * prevents all four arrows at its own entry, before working out whether
 * there is anywhere to go (`sortable.esm.js:669-670`), so the pair this
 * field routes to it already matches the donor at both ends of the strip.
 * Space and Escape are absent for the same kind of reason — `@dnd-kit`
 * prevents those itself.
 */
const KEYS_A_KEYBOARD_DRAG_SWALLOWS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
]);

/**
 * A cancel code no keyboard can produce.
 *
 * rbd calls a keyboard drag off on `mousedown`, `mouseup`, `click`,
 * `touchstart` and `wheel` (`use-keyboard-sensor.js:101-132`); `@dnd-kit`
 * binds only `resize` and `visibilitychange` and offers no imperative cancel.
 * What it does offer is the list of codes its keyboard sensor treats as a
 * cancel — so the abort is a synthetic `keydown` carrying a code nothing else
 * on the page answers to. Sending a real Escape would work too, and would
 * also close whatever dialog or popover the list sits inside.
 */
export const ABORT_KEYBOARD_DRAG = "AtlasAbortKeyboardDrag";

/** The events rbd treats as "the editor is no longer driving with the keys". */
const GESTURES_THAT_CALL_IT_OFF = ["mousedown", "mouseup", "click", "touchstart", "wheel"] as const;

/** rbd's sloppy-click threshold, measured per axis (`use-mouse-sensor.js:28-36`). */
const DONOR_LIFT_PX = 5;

/**
 * **A finger does not use the threshold above — it waits.**
 *
 * rbd has two sensors, not one, and the touch one shares nothing with the
 * mouse one: no sloppy click, no distance at all. A touch drag opens on a
 * **long press** of 120ms (`use-touch-sensor.js:48`, decreased from 150 to
 * work around iOS force-press), lifts at the point the finger went down, and
 * a finger that moves so much as a pixel before the timer fires is taken to
 * be scrolling and cancelled outright (`:124-130`). That asymmetry is the
 * whole of the touch gesture: without the wait, every attempt to scroll the
 * edit form past the strip picks a thumbnail up instead.
 */
const TOUCH_LONG_PRESS_MS = 120;

/**
 * `@dnd-kit`'s spelling of the **timer**, plus a backstop.
 *
 * A delay constraint is a `setTimeout(handleStart, delay)` and a tolerance
 * that cancels the pending press (`core.esm.js:1458-1461,1549-1553`), where
 * `hasExceededDistance` reads a plain number as `sqrt(dx² + dy²) > n`
 * (`:1043-1048`) — so a tolerance of zero cancels on any *movement*.
 *
 * That is close to rbd's rule but not it: rbd cancels on any `touchmove` at
 * all, moved or not (`use-touch-sensor.js:124-130`), and iOS sends one for a
 * change of pressure or contact radius under a stationary finger. The
 * donor's rule is the `touchmove` listener in `attach`; this tolerance is
 * what catches a `pointermove` that arrives without one.
 */
const TOUCH_LONG_PRESS: PointerActivationConstraint = { delay: TOUCH_LONG_PRESS_MS, tolerance: 0 };

const preventClickDefault = (event: Event) => event.preventDefault();

/** `pointerType` is not on `Event`, and `props.event` is typed as one. */
const isPointerEvent = (event: Event): event is PointerEvent => "pointerType" in event;
const startedWithAFinger = (event: Event) => isPointerEvent(event) && event.pointerType === "touch";

type Point = { x: number; y: number };

/**
 * The parts of `AbstractPointerSensor` the sensor below overrides.
 *
 * `@dnd-kit` marks its sensor internals `private`, which is a TypeScript
 * visibility annotation and nothing else: the compiled class keeps
 * `activated`, `initialCoordinates` and `props` as ordinary instance fields,
 * and `handleMove`, `handleStart`, `handleCancel`, `handlePending` and
 * `handleKeydown` as ordinary prototype methods. That is *why* its own
 * constructor can bind them (`core.esm.js:1416-1420`) — and binding reads
 * `this.handleMove`, so a subclass's override is what gets bound and what the
 * listener calls. TypeScript will not let a subclass redeclare a private
 * member, so the base is re-typed here once rather than each override being
 * cast at its use.
 *
 * Reaching past a `private` is a real cost and it is paid deliberately: the
 * alternative is the two behaviours below shipping as parity breaks. Both are
 * pinned by browser tests, so an upgrade that renames any of this fails
 * loudly rather than quietly restoring `@dnd-kit`'s own behaviour.
 */
interface PointerSensorInnards {
  /** `SensorInstance`'s one public member, and the reason this shape is
   * spelled out in full rather than intersected with `PointerSensor`:
   * `props` is private there and public here, and TypeScript reduces an
   * intersection with a name private on one side to `never`. */
  autoScrollEnabled: boolean;
  activated: boolean;
  initialCoordinates: Point | undefined;
  props: PointerSensorProps;
  /** Torn down wholesale by `detach`, which is what makes `attach` safe to
   * extend: anything added here goes when the press does. */
  windowListeners: {
    add(name: string, handler: EventListener, options?: AddEventListenerOptions): void;
  };
  documentListeners: {
    add(name: string, handler: EventListener, options?: AddEventListenerOptions): void;
  };
  attach(): void;
  handleStart(): void;
  handleCancel(): void;
  handlePending(constraint: PointerActivationConstraint, offset?: Point): void;
  handleMove(event: PointerEvent): void;
  handleKeydown(event: KeyboardEvent): void;
}

/** The slice of an element the refusal below reads, so a test can hand it a
 *  plain object. */
export interface PressElement {
  tagName: string;
  getAttribute(name: string): string | null;
  parentElement: PressElement | null;
}

/** `interactiveTagNames` (`react-beautiful-dnd.esm.js:6337-6346`). */
const INTERACTIVE_TAGS = new Set([
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "optgroup",
  "video",
  "audio",
]);

/**
 * **A press that starts on a control never lifts the row it sits in** — rbd's
 * `tryGetLock` refusal (`isAnInteractiveElement`, `esm:6348-6379`, applied at
 * `:6564` with `canDragInteractiveElements` off, as every donor list leaves
 * it): walked from the pressed element up to the handle, any of rbd's tags or
 * a `contenteditable` of `"true"` or `""` refuses. A link is *not* among them,
 * so a note's link lifts its card in the donor and here.
 *
 * Shared by every surface whose whole row or tile is the handle — the image
 * strip's ✕ and ‹ ›, a group card's chevron and ⋮, a row's menu — so the set
 * cannot drift between them.
 *
 * **A press that reaches the handle only through a portal is refused too.**
 * rbd listens on the window and finds the handle by walking the *page* up
 * from the target, so a press in a menu or a dialog a row opens — portalled
 * to the body — never finds the row. React hands the same press to the row's
 * own listeners, because a portal bubbles through the component tree; a walk
 * that never meets the handle is that press, and it lifts nothing.
 *
 * **A select drawn as MUI draws one is refused too** — #809's spec for the
 * Editor's status pill, a departure carried to its decision menu: rbd lifts
 * the row from the pill. rbd's `select` tag is a native control; MUI's
 * `Select` is a `div` standing for one, which the tag test misses. The
 * select's box — its display and, opened in place (`disablePortal`), its
 * menu — carries `data-dnd-interactive`, and the walk refuses it like the tag.
 *
 * **One departure, a named parity break**: rbd tests the target only when it
 * is an `HTMLElement` (`isEventInInteractiveElement`, `:6371-6377`), so a
 * press on a button's SVG glyph — most of an icon button — is not refused
 * there, and a 5px wobble on the chevron lifts the card. Here the walk starts
 * from whatever element was pressed, glyphs included, so a press on any part
 * of a button is the button's.
 *
 * **A button that stands for a bare glyph** carries `data-dnd-press-lifts`:
 * a pointer press on it lifts the row as a press on the donor's glyph does,
 * while the keyboard's Space on it, focused, stays the button's. The
 * changed-from-base info icon is one (#1083): a button for the keyboard and
 * its focus tooltip, a glyph to the pointer.
 */
export function isPressOnInteractiveElement(
  target: PressElement | null,
  handle?: PressElement | null,
  { pointer = false }: { pointer?: boolean } = {},
): boolean {
  for (let current = target; current; current = current.parentElement) {
    const lifts = pointer && current.getAttribute("data-dnd-press-lifts") !== null;
    if (!lifts && INTERACTIVE_TAGS.has(current.tagName.toLowerCase())) return true;
    const editable = current.getAttribute("contenteditable");
    if (editable === "true" || editable === "") return true;
    if (current.getAttribute("data-dnd-interactive") !== null) return true;
    if (current === handle) return false;
  }
  return handle != null;
}

const PointerSensorBase = PointerSensor as unknown as {
  new (props: PointerSensorProps): PointerSensorInnards;
  activators: typeof PointerSensor.activators;
};

/**
 * The donor's mouse sensor, written as `@dnd-kit`'s.
 *
 * Five things rbd's does that `@dnd-kit`'s does not:
 *
 *   - **it walks away from a press carrying a modifier** — ctrl, cmd, shift
 *     or alt (`use-mouse-sensor.js:226-229`) — because those are the page's
 *     own gestures, extend a selection or open in a new tab, and not the
 *     start of a drag. `@dnd-kit`'s activator checks `isPrimary` and `button`
 *     and nothing else, so shift-dragging a modsy thumbnail does nothing
 *     while the same gesture here reorders;
 *   - **its 5px threshold is per axis, not as the crow flies** —
 *     `|dx| >= 5 || |dy| >= 5` (`:28-36`). `@dnd-kit` reads a `distance`
 *     number as `sqrt(dx² + dy²) > n` and a `{x, y}` pair as an *and*
 *     (`core.esm.js:1043-1053`), so neither shape can spell the or: a
 *     diagonal 4/4px twitch is 5.66 as the crow flies and lifts a tile here
 *     that modsy leaves alone, and a flat 5px drag lifts in modsy and not
 *     here;
 *   - **any keystroke calls off a press that has not lifted yet**
 *     (`:144-149`). `@dnd-kit` answers only to Escape, and only ever by
 *     cancelling (`core.esm.js:1595-1598`);
 *   - **a window scroll calls off a press that has not lifted yet**
 *     (`:167-176`), so a thumbnail pressed and then wheel-scrolled past is
 *     forgotten rather than picked up by the next 5px;
 *   - **a second mouse button calls the whole thing off** — rbd cancels on
 *     any `mousedown` at all while a press is live (`:131-142`) and binds
 *     nothing to `contextmenu` on the mouse path, so right-clicking a lifted
 *     modsy tile sends it home *and* opens the browser menu. `@dnd-kit`
 *     listens for no second press and holds `contextmenu` shut for the whole
 *     gesture (`core.esm.js:1444-1448`); cancelling on the `pointerdown`
 *     that precedes it detaches that listener in time for the menu to open.
 *
 * The last two hang off `attach`, and are torn down by `detach` along with
 * everything `@dnd-kit` puts on the window itself.
 *
 * **And a finger gets rbd's other sensor.** `@dnd-kit` runs one pointer
 * sensor over every input; rbd runs a mouse sensor and a touch sensor with
 * separate rules, and `pointerType` is what tells them apart here. The four
 * touch rules ported — **owner ruling D4 for #378, 2026-09-22** — are the
 * 120ms long press (`attach`, via `TOUCH_LONG_PRESS`), the cancel on any
 * `touchmove` before it fires, the non-passive `touchmove` that blocks the
 * page from scrolling under a live drag (both the `touchmove` listener in
 * `attach`), and the `keydown` that calls a *live* touch drag off whatever
 * the key — where the mouse sensor answers only to Escape.
 *
 * It is a **best-effort port, and the owner licensed it as one**: a real
 * finger on a real device is not reachable from this suite, so what the
 * browser tests drive is synthesised `pointerType: "touch"` events. The
 * long press, the pre-lift cancel and the scroll block are pinned; the
 * keydown rule is not. Two donor behaviours are deliberately left out and
 * are the port's named gaps: force-press (`use-touch-sensor.js:178-233`), a
 * WebKit-only event this suite cannot raise, and `orientationchange`
 * (`:63-68`), because `@dnd-kit` already cancels on `resize` — which rbd's
 * own comment says is what most devices fire for it.
 */
class DonorPointerSensor extends PointerSensorBase {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: (event: React.PointerEvent, options: PointerSensorOptions) => {
        const { ctrlKey, metaKey, shiftKey, altKey } = event.nativeEvent;
        if (ctrlKey || metaKey || shiftKey || altKey) return false;
        if (
          isPressOnInteractiveElement(
            event.target as unknown as PressElement,
            event.currentTarget as unknown as PressElement,
            { pointer: true },
          )
        ) {
          return false;
        }
        return PointerSensor.activators[0].handler(event, options);
      },
    },
  ];

  /** `props.event` is the activator (`core.esm.js:3015-3019`); a getter rather
   * than a field because a field initialiser runs *after* `super()`, and the
   * base constructor calls `attach` from inside it. */
  get viaTouch() {
    return startedWithAFinger(this.props.event);
  }

  attach() {
    if (this.viaTouch) {
      // A fresh `props`, not a write into the options object below.
      // `useSensor` hands the same `options` it was given straight to every
      // sensor it builds, and nothing promises that object is per-press — so
      // writing a delay into it would risk leaving the *mouse* path waiting
      // 120ms. (Today it happens to be rebuilt each render, which is why no
      // test here can tell the two apart; the copy is what makes the sensor
      // independent of that.)
      this.props = {
        ...this.props,
        options: { ...this.props.options, activationConstraint: TOUCH_LONG_PRESS },
      };
    }

    super.attach();

    if (this.viaTouch) {
      this.windowListeners.add(
        "touchmove",
        (event) => {
          // **Before the lift, a `touchmove` is the end of it**
          // (`use-touch-sensor.js:124-130`). rbd does not measure the move:
          // a finger that has touched and then sent anything at all is
          // taken to be scrolling the form, and the pending press is let
          // go without its default prevented, so the scroll proceeds.
          if (!this.activated) {
            this.handleCancel();
            return;
          }

          // **After it, the page is held still for the length of the drag**
          // (`:119-145`). `@dnd-kit` already prevents the `pointermove` it
          // moves on (`core.esm.js:1560-1562`), but preventing a
          // `pointermove` does not stop a scroll — only a non-passive
          // `touchmove` does, which is why rbd opts out of the passive
          // default for this one listener and says so in a comment.
          if (event.cancelable) event.preventDefault();
        },
        // rbd's own options, including the window as the target — its
        // comment at `:266-271` says the handle was right on older iOS and
        // the window is right now — and `capture: false`, which it pins to
        // a Firefox bug rather than to a preference (`:121-123`).
        { passive: false, capture: false },
      );
    }

    this.windowListeners.add(
      "scroll",
      () => {
        if (!this.activated) this.handleCancel();
      },
      // rbd's own options for this one: passive, and not on capture.
      { passive: true, capture: false },
    );
    this.windowListeners.add(
      "mousedown",
      (event) => {
        // `mousedown`, not `pointerdown`: a second button pressed while the
        // pointer is already down is a `pointermove` with new `buttons`, not
        // another `pointerdown` — mouse events are the only ones that still
        // report it per button, which is the event rbd listens on too.
        //
        // And only a non-primary one. rbd cancels on any `mousedown` at all,
        // but it is started *from* a `mousedown`, so the press that opened
        // the drag is already past by the time it is listening. This sensor
        // opens on the `pointerdown` that precedes it, so its own press's
        // `mousedown` is still to come — and a second *primary* press cannot
        // happen while the first is held, which makes the two rules the same
        // rule.
        if ((event as MouseEvent).button !== 0) {
          this.handleCancel();
          return;
        }
        // **The press itself is consumed, exactly as rbd consumes it.** Once
        // `tryGetLock` hands back a lock, rbd calls `event.preventDefault()`
        // on the `mousedown` under the comment "consuming the event"
        // (`use-mouse-sensor.js:248-249`), from a `capture: true` binding on
        // `window` (`:303-315`). A prevented `mousedown` does not move focus,
        // so a donor thumbnail is never focused by a press — only by Tab, or
        // by rbd restoring what was focused *before* the lift
        // (`use-focus-marshal.js:88-105`, `state/middleware/focus.js:10-16`).
        //
        // `@dnd-kit` prevents nothing here, and the tile is a `tabIndex: 0`
        // handle, so without this a click leaves the figure focused — which
        // satisfies `:focus-within` and pins the ‹ › bar open on that tile
        // long after the pointer has gone, and leaves a Space straight after
        // a mouse drag lifting the tile again where modsy scrolls the page.
        //
        // This lands on the sensor's *own* press: `attach` runs inside the
        // `pointerdown` React handler, and the compatibility `mousedown`
        // follows it. A press that starts on the ✕ or the ‹ › never gets
        // here at all — the activator's `isPressOnInteractiveElement` refuses
        // it before the sensor exists — and that is the donor's rule too, because `tryGetLock`
        // refuses an interactive element and returns *before* the
        // `preventDefault` (`use-sensor-marshal.js:182-189`).
        event.preventDefault();
      },
      // Capture, so the cancel — and with it `detach`, which takes
      // `@dnd-kit`'s `contextmenu` guard down with it — runs before the
      // `contextmenu` a right-click sends a moment later.
      { capture: true },
    );
  }

  /**
   * **The click a drop leaves behind is defaulted, not stopped.** Both
   * libraries keep it from acting — a link released over is not followed —
   * but rbd only `preventDefault`s it (`esm:6614-6626`), so it still travels
   * the page and a listener on the document hears it: the group menu's
   * click-away closes on it in the donor. `@dnd-kit` stops its propagation
   * at the document instead (`core.esm.js:1506-1508`). Its own `click`
   * listener, swapped for rbd's as it is added; `@dnd-kit` still takes it
   * down 50ms after the drop, where rbd waits a tick.
   */
  handleStart() {
    pointerLiftPoint = this.initialCoordinates;
    const listeners = this.documentListeners;
    const add = listeners.add;
    listeners.add = (name, handler, options) =>
      add.call(listeners, name, name === "click" ? preventClickDefault : handler, options);
    try {
      super.handleStart();
    } finally {
      listeners.add = add;
    }
  }

  /**
   * Only the pre-lift half is ours. Once the tile is in the air the threshold
   * has already been cleared and there is nothing left to disagree about.
   */
  handleMove(event: PointerEvent) {
    const start = this.initialCoordinates;
    const constraint = this.props.options.activationConstraint;
    // A finger is `super`'s all the way: `TOUCH_LONG_PRESS` is a delay
    // constraint, and the base already runs it as rbd's touch sensor does —
    // cancel on any movement while pending, plain `onMove` once lifted. The
    // 5px below is the *mouse* threshold, and re-seating the grab point is
    // its compensation; neither exists on the donor's touch path.
    if (this.viaTouch || this.activated || !start || !constraint) {
      super.handleMove(event);
      return;
    }

    const travelled = { x: start.x - event.clientX, y: start.y - event.clientY };
    if (Math.abs(travelled.x) >= DONOR_LIFT_PX || Math.abs(travelled.y) >= DONOR_LIFT_PX) {
      // **Lifted at where the pointer is now, not at where it went down.**
      // rbd hands `fluidLift` the current point and says why: "Lifting at the
      // current point to prevent the draggable item from jumping by the
      // sloppyClickThreshold" (`use-mouse-sensor.js:102-112`).
      // `handleStart` dispatches `this.initialCoordinates`, and every later
      // offset is measured from it, so leaving the `pointerdown` point there
      // spends the 5px twice — the tile stands still while the threshold is
      // crossed and then jumps the whole 5px on the next move, and the grab
      // point sits 5px off the donor's for the rest of the drag.
      this.initialCoordinates = { x: event.clientX, y: event.clientY };
      this.handleStart();
      return;
    }

    // Still pending — reported rather than dropped, so `@dnd-kit`'s own
    // pending bookkeeping stays in step. What is deliberately *not* done here
    // is falling through to `super`, which would measure the same move against
    // its own threshold and lift the diagonal twitch this override refuses.
    this.handlePending(constraint, travelled);
  }

  handleKeydown(event: KeyboardEvent) {
    // A *live* touch drag is called off by any key at all, and only Escape
    // has its default prevented — rbd's own comment calls that the
    // difference between a direct cancel and an indirect one
    // (`use-touch-sensor.js:83-100`).
    if (this.viaTouch && this.activated) {
      if (event.code === "Escape") event.preventDefault();
      this.handleCancel();
      return;
    }
    // A press that has not lifted yet dies on any key whichever input it
    // came from — rbd's mouse sensor (`use-mouse-sensor.js:144-149`) and the
    // not-`DRAGGING` branch of the touch handler above agree on that.
    if (!this.activated) {
      this.handleCancel();
      return;
    }
    super.handleKeydown(event);
  }
}

/**
 * The sentences every drag handle's `aria-describedby` points at
 * (`screen-reader-message-preset.js:18-22`), collapsed to one line the way each
 * list's announcements are.
 *
 * `@dnd-kit` ships its own wording for the same node and names the same keys,
 * so it would be easy to read the two as equivalent. They are not: the donor's
 * third sentence is the one that tells a JAWS or NVDA user why space bar does
 * nothing until they leave browse mode, and `@dnd-kit`'s text has no
 * equivalent of it at all.
 */
export const DRAG_HANDLE_INSTRUCTIONS =
  "Press space bar to start a drag. " +
  "When dragging you can use the arrow keys to move the item around and escape to cancel. " +
  "Some screen readers may require you to be in focus mode or to use your pass through key";

/**
 * The donor's two motion curves, and the z-indexes they run at.
 *
 * Spelled out rather than left to `@dnd-kit`'s defaults (200ms `ease` for the
 * sort, 250ms `ease` for the drop), because these are the values the slice
 * exists to reproduce. Each was read twice: off `react-beautiful-dnd`'s own
 * `animation.js` (`outOfTheWay` = `0.2s cubic-bezier(0.2, 0, 0, 1)`, `drop` =
 * `cubic-bezier(.2,1,.1,1)`, `minDropTime` = `0.33s`) and off the running donor
 * mid-gesture, where a displaced tile computes to
 * `transform 0.2s cubic-bezier(0.2, 0, 0, 1)` and the settling tile to
 * `transform, opacity 0.33s cubic-bezier(0.2, 1, 0.1, 1)` at `z-index: 4500`.
 */
export const SIBLING_SHIFT = { duration: 200, easing: "cubic-bezier(0.2, 0, 0, 1)" } as const;
/** The same curve spelled for a `transition` property rather than `@dnd-kit`. */
export const SIBLING_TRANSITION = `transform ${SIBLING_SHIFT.duration}ms ${SIBLING_SHIFT.easing}`;
const DROP_SETTLE_MS = 330;
const MAX_DROP_SETTLE_MS = 550;
/** The travel at which the settle reaches its longest (`get-drop-duration.js:15`). */
const LONGEST_SETTLE_AT_PX = 1500;
/** rbd multiplies the settle by 0.6 when the reason is `CANCEL` — but see below. */
const CANCEL_MODIFIER = 0.6;
export const DROP_EASING = "cubic-bezier(0.2, 1, 0.1, 1)";
/** `get-style.js:17-18` — the lifted tile clears the page; the settling one drops a layer. */
export const LIFTED_Z_INDEX = 5000;
export const SETTLING_Z_INDEX = 4500;

/**
 * How long the tile takes to land, in milliseconds — `get-drop-duration.js`.
 *
 * **The 0.6 is not unconditional.** rbd scales the settle with how far the
 * tile still has to travel, from 0.33s at zero to 0.55s at 1500px, and *then*
 * shortens a cancel, so only a cancel that carried the tile somewhere snaps
 * back faster. The two-decimal rounding is rbd's own, and it is what makes
 * this strip's numbers whole: a two-slot cancel is 0.22s rather than 0.2177s.
 *
 * **The `travel <= 0` line is not a floor.** It mirrors rbd's, but in rbd it
 * is unreachable for either list: `drop-middleware.js:121-131` skips the whole
 * animation when the tile is already home, and `getDropDuration` is called
 * only after that guard — so the early return is reachable only through
 * `combine`, which neither list uses. An earlier revision of this comment
 * read the branch without the guard in front of it and concluded that a
 * lift-then-Escape settles over the full 330ms. It does not settle at all,
 * and both settles (`settleTheTile`, the sort rows' `settle`) short-circuit the
 * same way.
 */
export function settleOver(travel: number, cancelled: boolean) {
  if (travel <= 0) return DROP_SETTLE_MS;
  if (travel >= LONGEST_SETTLE_AT_PX) return MAX_DROP_SETTLE_MS;

  const range = (MAX_DROP_SETTLE_MS - DROP_SETTLE_MS) / 1000;
  const seconds = DROP_SETTLE_MS / 1000 + range * (travel / LONGEST_SETTLE_AT_PX);
  return Number((cancelled ? seconds * CANCEL_MODIFIER : seconds).toFixed(2)) * 1000;
}

/**
 * rbd 13.1.1's fluid auto-scroller — the pointer drag's edge scroll
 * (`state/auto-scroller/fluid-scroller/config.js`), ported for the Editor's
 * drag (#869 D4 a) in place of `@dnd-kit`'s own: 20% from the edge, a linear
 * ramp keyed on the pointer, no ease-in. rbd keys on the dragged box's centre
 * instead, starts 25% from the edge, peaks at 28px a step 5% from it on a
 * quadratic, and eases in over the first 1.2s of a drag lifted inside a zone.
 */
const FLUID_START_FROM = 0.25;
const FLUID_MAX_AT = 0.05;
const FLUID_MAX_PX = 28;
const FLUID_MIN_PX = 1;
const FLUID_ACCELERATE_AT_MS = 360;
const FLUID_STOP_DAMPENING_AT_MS = 1200;

/** `get-percentage.js`: where `current` sits from `start` to `end`, 0 for an empty range. */
function percentage(start: number, end: number, current: number) {
  const range = end - start;
  return range === 0 ? 0 : (current - start) / range;
}

/**
 * One step's scroll, in px, for a dragged box whose centre is `distance` from
 * a container edge `size` long — `get-value-from-distance.js`: nothing past
 * the zone, 1px on its line, the peak inside the last 5% (and past the edge),
 * and `ceil(28 × (1 − p)²)` between, `p` the way from the peak line to the
 * zone's.
 */
export function fluidScrollStep(distance: number, size: number): number {
  const startFrom = size * FLUID_START_FROM;
  const maxAt = size * FLUID_MAX_AT;
  if (distance > startFrom) return 0;
  if (distance <= maxAt) return FLUID_MAX_PX;
  if (distance === startFrom) return FLUID_MIN_PX;
  const fromStart = 1 - percentage(maxAt, startFrom, distance);
  return Math.ceil(FLUID_MAX_PX * fromStart ** 2);
}

/**
 * A step eased in by the time since the lift — `dampen-value-by-time.js` under
 * `get-value.js`'s 1px floor: 1px before 360ms, the full step from 1200ms, and
 * `ceil(step × p²)` between. No step stays none.
 */
export function dampenFluidScroll(step: number, msSinceLift: number): number {
  if (step === 0) return 0;
  if (msSinceLift >= FLUID_STOP_DAMPENING_AT_MS) return step;
  if (msSinceLift < FLUID_ACCELERATE_AT_MS) return FLUID_MIN_PX;
  const eased = percentage(FLUID_ACCELERATE_AT_MS, FLUID_STOP_DAMPENING_AT_MS, msSinceLift);
  return Math.max(Math.ceil(step * eased ** 2), FLUID_MIN_PX);
}

/**
 * The vertical scroll a container needs this step, signed (down positive) —
 * `get-scroll/index.js` on one axis: only the nearer edge counts
 * (`get-scroll-on-axis`), the time dampening only for a drag `dampen`ed at
 * its lift, and none at all for a dragged box taller than the container
 * (`adjust-for-size-limits.js`, the box's height at the lift).
 */
export function fluidScrollChange({
  frame,
  center,
  subjectHeight,
  msSinceLift,
  dampen,
}: {
  /** The container's visible box, top and bottom, in viewport px. */
  frame: { top: number; bottom: number };
  /** The dragged box's centre, in the same px. */
  center: number;
  subjectHeight: number;
  msSinceLift: number;
  dampen: boolean;
}): number {
  if (subjectHeight > frame.bottom - frame.top) return 0;
  const toTop = center - frame.top;
  const toBottom = frame.bottom - center;
  const towardEnd = toBottom < toTop;
  const step = fluidScrollStep(towardEnd ? toBottom : toTop, frame.bottom - frame.top);
  const value = dampen ? dampenFluidScroll(step, msSinceLift) : step;
  return value === 0 ? 0 : towardEnd ? value : -value;
}

/** `can-scroll.js`'s `canPartiallyScroll` on one axis: 1px of room that way. */
function canScrollBy(change: number, current: number, max: number) {
  if (change === 0) return false;
  return change > 0 ? current < max : current > 0;
}

/** A box's four edges, in viewport px. */
export interface Edges {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Whether the dragged `box` is over a vertical `list` — `get-droppable-over.js`,
 * the list's visible area as `list`: the two overlap, and the box's centre is
 * inside, or it spans the list across, or the one edge inside has not crossed
 * the list's middle.
 */
export function isOverList(box: Edges, list: Edges): boolean {
  const overlaps =
    box.left < list.right &&
    box.right > list.left &&
    box.top < list.bottom &&
    box.bottom > list.top;
  if (!overlaps) return false;
  const x = (box.left + box.right) / 2;
  const y = (box.top + box.bottom) / 2;
  if (x >= list.left && x <= list.right && y >= list.top && y <= list.bottom) return true;
  const middle = (list.left + list.right) / 2;
  const leftIn = box.left >= list.left && box.left <= list.right;
  const rightIn = box.right >= list.left && box.right <= list.right;
  if (!leftIn && !rightIn) return true;
  return leftIn ? box.left < middle : box.right > middle;
}

/** The part of `inner` inside `outer`, or none. */
function clip(inner: Edges, outer: Edges): Edges | null {
  const top = Math.max(inner.top, outer.top);
  const bottom = Math.min(inner.bottom, outer.bottom);
  const left = Math.max(inner.left, outer.left);
  const right = Math.min(inner.right, outer.right);
  return top < bottom && left < right ? { top, bottom, left, right } : null;
}

/** A scroll container by `get-closest-scrollable.js`'s rule. */
function isScrollContainer(element: HTMLElement) {
  const { overflowY } = getComputedStyle(element);
  return overflowY === "auto" || overflowY === "scroll";
}

/**
 * Where the pointer stood when the latest pointer drag lifted, in viewport px
 * — the point rbd's `fluidLift` is handed, which its lift dry run measures.
 */
let pointerLiftPoint: Point | undefined;

/** Frames from what re-runs rbd's scroll to the step it lands (see below). */
const FLUID_STEP_LAG_FRAMES = 2;

/**
 * Runs rbd's fluid auto-scroll while a pointer drag is `live`: the dragged box
 * (`lifted`, the overlay's copy, measured once at the lift) carried with the
 * pointer, and — in rbd's order (`scroll.js`) — the window scrolled against
 * the viewport if it needs to and can, else `region` against its visible box
 * while the box is over one of `lists` (`isOverList`) or its centre is inside
 * that box. The drop or cancel stops it.
 *
 * **A step is what an event re-runs, two frames on** — measured on the donor
 * (#869 D4 a): rbd scrolls from its store's middleware, so only a pointer move
 * or a scroll of the window or the region runs it, and each reaches the page
 * through two `raf-schd` hops. A step that scrolls fires the scroll event that
 * runs the next, so a still pointer steps every other frame, and one that
 * scrolls nothing ends the run until the pointer moves.
 *
 * **The lift's dry run measures the pointer, not the box** (`fluid-scroller/
 * index.js`): rbd's lift sets the drag's page centre to the lift point
 * (`state/reducer.js`, `INITIAL_PUBLISH`), and only the first update after it
 * puts the box's centre there. A pointer lifted inside a zone dampens the
 * whole drag and takes one step at once; the step's own scroll then measures
 * the box, as every step after it does.
 */
export function useDonorFluidAutoScroll(
  live: boolean,
  lifted: { readonly current: HTMLElement | null },
  region: HTMLElement | null,
  lists: () => readonly HTMLElement[],
) {
  // The latest `lists`, read when a step runs: a caller's inline function
  // must not restart the drag's run on every render.
  const listsRef = useRef(lists);
  useEffect(() => {
    listsRef.current = lists;
  });

  useEffect(() => {
    if (!live) return;
    const liftedAt = performance.now();
    const lift = pointerLiftPoint;
    let pointer = lift;
    // The box less the pointer, fixed at the lift: rbd moves the box with the
    // pointer in its state, never by measuring the copy, which may not have
    // rendered the latest move yet.
    let grab: Edges | null = null;
    let dampen = false;
    let frame = 0;
    let dueAt: number | null = null;
    let frameId = 0;

    const plan = (box: Edges, msSinceLift: number, eased: boolean, overHome: boolean) => {
      const root = document.documentElement;
      const scrollOf = (frameEdges: { top: number; bottom: number }) =>
        fluidScrollChange({
          frame: frameEdges,
          center: (box.top + box.bottom) / 2,
          subjectHeight: box.bottom - box.top,
          msSinceLift,
          dampen: eased,
        });
      const windowChange = scrollOf({ top: 0, bottom: root.clientHeight });
      const windowMax = root.scrollHeight - root.clientHeight;
      if (canScrollBy(windowChange, window.scrollY, windowMax)) {
        return { scroll: () => window.scrollBy(0, windowChange) };
      }
      if (!region || !isScrollContainer(region)) return null;
      const visible = region.getBoundingClientRect();
      // rbd scrolls the frame of the list the box is over — at the lift the
      // home list, over by definition — else the frame its centre is in
      // (`get-best-scrollable-droppable.js`); every list here is in `region`.
      const center = { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 };
      const over =
        overHome ||
        (center.x >= visible.left &&
          center.x <= visible.right &&
          center.y >= visible.top &&
          center.y <= visible.bottom) ||
        listsRef.current().some((list) => {
          const area = clip(list.getBoundingClientRect(), visible);
          return area !== null && isOverList(box, area);
        });
      if (!over) return null;
      const regionChange = scrollOf(visible);
      const regionMax = region.scrollHeight - region.clientHeight;
      if (!canScrollBy(regionChange, region.scrollTop, regionMax)) return null;
      return {
        scroll: () => {
          region.scrollTop += regionChange;
        },
      };
    };

    const rerun = () => {
      const at = frame + FLUID_STEP_LAG_FRAMES;
      dueAt = dueAt === null ? at : Math.min(dueAt, at);
    };
    const moved = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
      rerun();
    };
    const boxAt = (at: Point, offset: Edges): Edges => ({
      top: at.y + offset.top,
      bottom: at.y + offset.bottom,
      left: at.x + offset.left,
      right: at.x + offset.right,
    });

    const tick = () => {
      frame += 1;
      frameId = requestAnimationFrame(tick);
      if (grab === null) {
        const measured = lifted.current?.getBoundingClientRect();
        if (!measured) return;
        const at = lift ?? {
          x: (measured.left + measured.right) / 2,
          y: (measured.top + measured.bottom) / 2,
        };
        pointer ??= at;
        grab = {
          top: measured.top - at.y,
          bottom: measured.bottom - at.y,
          left: measured.left - at.x,
          right: measured.right - at.x,
        };
        // The dry run's box: the lifted one, centred on the lift point.
        const half = { y: measured.height / 2, x: measured.width / 2 };
        const dry = {
          top: at.y - half.y,
          bottom: at.y + half.y,
          left: at.x - half.x,
          right: at.x + half.x,
        };
        dampen = plan(dry, 0, false, true) !== null;
        if (dampen) plan(dry, performance.now() - liftedAt, true, true)?.scroll();
        return;
      }
      if (dueAt === null || frame < dueAt || pointer === undefined) return;
      dueAt = null;
      plan(boxAt(pointer, grab), performance.now() - liftedAt, dampen, false)?.scroll();
    };
    frameId = requestAnimationFrame(tick);
    window.addEventListener("pointermove", moved, { capture: true });
    window.addEventListener("scroll", rerun);
    region?.addEventListener("scroll", rerun);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("pointermove", moved, { capture: true });
      window.removeEventListener("scroll", rerun);
      region?.removeEventListener("scroll", rerun);
    };
  }, [live, lifted, region]);
}

/**
 * The keyboard sensor's codes. **Space lifts, Space drops, Escape cancels —
 * and Enter does nothing.** `@dnd-kit` binds Enter alongside Space by default;
 * rbd binds only Space (`sensors/keyboard-sensor.js`), so on a handle that is
 * also a `role="button"` Enter falls through to the page, as it does in the
 * donor. The abort code is the synthetic cancel above.
 */
const DONOR_KEYBOARD_CODES = {
  start: ["Space"],
  cancel: ["Escape", ABORT_KEYBOARD_DRAG],
  end: ["Space"],
};

/**
 * The donor's keyboard sensor's lift, written as `@dnd-kit`'s.
 *
 * rbd listens for the Space on the window, in the capture phase, and lifts the
 * handle closest to whatever has the focus (`use-keyboard-sensor.js`,
 * `esm:5983-6011`), refusing only what it refuses a press
 * (`isPressOnInteractiveElement`). `@dnd-kit`'s activator lifts only when the
 * key lands on the activator itself (`core.esm.js:1358-1360`). On a surface
 * whose whole row is the handle the two part company over everything
 * focusable inside the row that is not one of rbd's tags — a note's link, a
 * `role="button"` span — where the donor lifts the row and `@dnd-kit` would
 * leave the Space to the page.
 */
class DonorKeyboardSensor extends KeyboardSensor {
  static activators = [
    {
      eventName: "onKeyDown" as const,
      handler: (
        event: React.KeyboardEvent,
        { keyboardCodes = DONOR_KEYBOARD_CODES, onActivation }: KeyboardSensorOptions,
        { active }: { active: { activatorNode: { current: HTMLElement | null } } },
      ) => {
        if (!keyboardCodes.start.includes(event.nativeEvent.code)) return false;
        const handle = active.activatorNode.current ?? event.currentTarget;
        if (
          isPressOnInteractiveElement(
            event.target as unknown as PressElement,
            handle as unknown as PressElement,
          )
        ) {
          return false;
        }
        event.preventDefault();
        onActivation?.({ event: event.nativeEvent });
        return true;
      },
    },
  ];
}

/** Where a drag stands — rbd's phases (`esm:4916-4927`), for the handles. */
export type DonorDragPhase = "resting" | "dragging" | "dropAnimating" | "userCancel";

/**
 * rbd's drag-handle rules (`react-beautiful-dnd.esm.js:4916-4927`), for a row
 * or card that is its own handle:
 *
 *   - **always**: no long-press callout, no tap highlight, and `touch-action:
 *     manipulation` to drop the double-tap zoom delay;
 *   - **resting** and **dropAnimating**: the `grab` cursor. modsy writes the
 *     resting rule into a stylesheet it fills only after the first drag, so
 *     a cold modsy page shows the arrow — a dev-only effect of `memoizeOne`
 *     under StrictMode; production shows the hand. Atlas applies it from the
 *     first paint, as #378 PB-1 rules for the image strip;
 *   - **dragging**: no pointer events on any handle, so nothing under the
 *     pointer hovers or answers while the item is in the air — the body's
 *     `grabbing` (`useDonorDragBodyRules`) is the cursor throughout;
 *   - **userCancel**: none of the state rules — the arrow while a cancelled
 *     item snaps home.
 */
export function donorHandleClasses(phase: DonorDragPhase) {
  return cn(
    "touch-manipulation [-webkit-tap-highlight-color:rgba(0,0,0,0)] [-webkit-touch-callout:none]",
    (phase === "resting" || phase === "dropAnimating") && "cursor-grab",
    phase === "dragging" && "pointer-events-none",
  );
}

/**
 * The donor's two sensors, for a surface that says which way its arrows walk.
 *
 * 5px is the donor's `sloppyClickThreshold` — the distance rbd requires before
 * a press becomes a drag rather than a click
 * (`create-sensor/util/is-sloppy-click-threshold-exceeded.js`); the sensor
 * reads it per axis, as rbd does.
 */
export function useDonorSensors(coordinateGetter: KeyboardCoordinateGetter) {
  return useSensors(
    useSensor(DonorPointerSensor, { activationConstraint: { distance: DONOR_LIFT_PX } }),
    useSensor(DonorKeyboardSensor, { coordinateGetter, keyboardCodes: DONOR_KEYBOARD_CODES }),
  );
}

/**
 * The keys the donor swallows for the length of a drag, and the mouse and wheel
 * activity that calls a *keyboard* drag off.
 *
 * Tab and Enter are swallowed for **either** kind of drag, the rest for a
 * keyboard one — capture phase on the window, as rbd binds it, so the page
 * never sees the key; `@dnd-kit`'s own listener is on the document and runs
 * after, and does not look at `defaultPrevented`, so gating these here costs
 * it nothing.
 *
 * A pointer drag has a held button and ends when it comes up; a keyboard drag
 * has neither, so rbd stops it the moment the user reaches for the mouse
 * instead (`use-keyboard-sensor.js:101-132`).
 */
export function useDonorDragKeys(dragging: boolean, keyboardDrag: boolean) {
  useEffect(() => {
    if (!dragging) return;

    const swallow = (event: KeyboardEvent) => {
      const swallowed =
        KEYS_ANY_DRAG_SWALLOWS.has(event.key) ||
        (keyboardDrag && KEYS_A_KEYBOARD_DRAG_SWALLOWS.has(event.key));
      if (swallowed) event.preventDefault();
    };

    window.addEventListener("keydown", swallow, { capture: true });
    return () => window.removeEventListener("keydown", swallow, { capture: true });
  }, [dragging, keyboardDrag]);

  useEffect(() => {
    if (!dragging || !keyboardDrag) return;

    const callItOff = () => {
      document.dispatchEvent(
        new KeyboardEvent("keydown", { code: ABORT_KEYBOARD_DRAG, bubbles: true }),
      );
    };

    for (const gesture of GESTURES_THAT_CALL_IT_OFF) {
      window.addEventListener(gesture, callItOff, { capture: true, passive: true });
    }
    return () => {
      for (const gesture of GESTURES_THAT_CALL_IT_OFF) {
        window.removeEventListener(gesture, callItOff, { capture: true });
      }
    };
  }, [dragging, keyboardDrag]);
}

/**
 * The grabbing cursor, the selection lock and no scroll anchoring, on
 * `<body>`, for the length of the drag — rbd's `dragging` rules, which it writes into a stylesheet of
 * its own (`get-styles.js:87,146-159`).
 *
 * **It has to be the page, not the item.** The lifted item is
 * `pointer-events: none`, so hit-testing falls through to whatever is
 * beneath it and the cursor comes from there — the form's background, a
 * text field, the page margin. Styling only the item leaves a drag that
 * shows `grab` for one frame and then an arrow for the rest of the gesture.
 * `user-select` is the same rule's other half: without it, a drag across the
 * page selects the labels it passes over.
 */
export function useDonorDragBodyRules(dragging: boolean) {
  useEffect(() => {
    if (!dragging) return;
    const { style } = document.body;
    const resting = {
      cursor: style.cursor,
      userSelect: style.userSelect,
      overflowAnchor: style.overflowAnchor,
    };
    style.cursor = "grabbing";
    style.userSelect = "none";
    // The third rule in the same block. Reordering moves content above the
    // fold, which is exactly what scroll anchoring exists to compensate for —
    // and on a scrolled form that compensation fights the drag.
    style.overflowAnchor = "none";
    return () => {
      style.cursor = resting.cursor;
      style.userSelect = resting.userSelect;
      style.overflowAnchor = resting.overflowAnchor;
    };
  }, [dragging]);
}
